import { useEffect, useState } from 'react'
import { TextInput } from '@forwardflow/ui-v2'
import { CheckCircle02Icon, DataIcon } from '@forwardflow/icons'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useAppStore } from '@forwardflow/store/appStore'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { DEV_DUMMY, isFreeFlow, useDevTools } from '../dev/devTools'
import { Card, LinkButton, StepFooter, StepHeader } from '../shell/Card'

/** Where a Follow Up Boss admin creates an API key. */
const FUB_API_KEY_URL = 'https://app.followupboss.com/2/api'

/**
 * Step 1: the Follow Up Boss API key.
 *
 * The key is what lets every later screen open pre-filled (team, area code,
 * stop rules, business details). The organization is created here when
 * there is none yet. The key is verified as it is typed and one button both
 * connects and moves on.
 */
export const FubConnect = () => {
   const [fubApiKey, setFubApiKey] = useState('')
   const { getNextStep, onboardingData, setIsFUBConnected, setOrganizationId } =
      useOnboardingStoreV2()
   const { setCurrentOrganization } = useAppStore()
   const { organizationId, isFUBConnected, email } = onboardingData || {}
   const [isLoading, setIsLoading] = useState(false)
   const [error, setError] = useState<string | null>(null)
   const [isVerifying, setIsVerifying] = useState(false)
   const [isValid, setIsValid] = useState(false)
   const [showHelp, setShowHelp] = useState(false)

   useDevTools({
      fill: () => setFubApiKey(DEV_DUMMY.fubApiKey),
      skip: () => {
         setIsFUBConnected(true)
         getNextStep()
         return true
      },
   })

   // Verify the key as it is typed (debounced).
   useEffect(() => {
      setIsValid(false)
      setError(null)
      if (isFreeFlow()) {
         setIsValid(true)
         setIsVerifying(false)
         return
      }
      if (!fubApiKey.trim()) {
         setIsVerifying(false)
         return
      }
      setIsVerifying(true)
      const timeoutId = setTimeout(async () => {
         try {
            const response = await OnboardingV2Service.verifyFubApiKey({
               token: fubApiKey.trim(),
            })
            if (response?.data?.isCrmApiKeyValid === true) {
               setIsValid(true)
               setError(null)
            } else if (response?.data?.isCrmApiKeyValid === false) {
               setError("That key didn't work. Check it in Follow Up Boss and paste it again.")
            }
         } catch {
            setError("We couldn't check the key. Try again in a moment.")
         } finally {
            setIsVerifying(false)
         }
      }, 500)
      return () => clearTimeout(timeoutId)
   }, [fubApiKey])

   /**
    * Connect and move on. With no organization yet (a brand-new sign-up),
    * create it first, save the key, then write the record the loader will
    * read back when the organization switches.
    */
   const handleConnect = async () => {
      if (isFUBConnected) {
         getNextStep()
         return
      }
      if (isFreeFlow()) {
         setIsFUBConnected(true)
         getNextStep()
         return
      }
      const token = fubApiKey.trim()
      if (!token || !isValid) return
      try {
         setIsLoading(true)
         setError(null)
         let orgId = organizationId
         const isNewOrganization = !orgId
         if (!orgId) {
            if (!email) {
               setError('Sign in again before connecting Follow Up Boss.')
               return
            }
            const started = await OnboardingV2Service.startOnboarding({ email })
            orgId = started?.data?.id || null
            if (!started?.success || !orgId) {
               setError("We couldn't create your account. Try again.")
               return
            }
         }
         const response = await OnboardingV2Service.submitFubApiKey({
            organizationId: orgId,
            token,
         })
         if (!response?.success) {
            setError("We couldn't connect Follow Up Boss. Try again.")
            return
         }
         if (isNewOrganization) {
            const record = useOnboardingStoreV2.getState().onboardingData
            await OnboardingV2Service.updateOnboardingState({
               organizationId: orgId,
               data: {
                  ...record,
                  organizationId: orgId,
                  isFUBConnected: true,
                  currentStep: 'you-and-team',
               },
            })
            setOrganizationId(orgId)
            setIsFUBConnected(true)
            getNextStep()
            setCurrentOrganization(orgId)
            return
         }
         setIsFUBConnected(true)
         getNextStep()
      } catch {
         setError("We couldn't connect Follow Up Boss. Try again.")
      } finally {
         setIsLoading(false)
      }
   }

   const buttonLabel = isFUBConnected
      ? 'Continue'
      : isLoading
        ? 'Connecting'
        : !fubApiKey.trim() && !isFreeFlow()
          ? 'Paste your API key to continue'
          : isVerifying
            ? 'Checking the key'
            : error
              ? 'Try another key'
              : 'Connect Follow Up Boss'

   const canContinue =
      isFUBConnected || isFreeFlow() || (isValid && !isVerifying && !isLoading && !error)

   return (
      <div className="space-y-4">
         <Card>
            <StepHeader
               title="Connect your Follow Up Boss account"
               lede="Paste one API key. We read your team, your stages and tags, and your lead sources, so every screen after this one is already filled in. You just confirm. We never change anything in Follow Up Boss unless you tell us to."
            />

            {isFUBConnected ? (
               <div
                  className="flex items-center gap-3 rounded-xl border border-[#9F7BFF]/30 bg-[#9F7BFF]/10 px-4 py-3"
                  data-testid="fub-connected"
               >
                  <CheckCircle02Icon width={22} height={22} color="#00D492" />
                  <div>
                     <p className="text-white text-sm font-medium">Follow Up Boss is connected</p>
                     <p className="text-white/50 text-xs">
                        Your team, stages and leads are syncing in the background.
                     </p>
                  </div>
               </div>
            ) : (
               <div className="space-y-3">
                  <div className="flex items-center justify-between gap-4">
                     <p className="text-white/90 text-sm font-medium">Follow Up Boss API key</p>
                     <LinkButton onClick={() => setShowHelp((v) => !v)}>
                        {showHelp ? 'Hide help' : 'Where is my key?'}
                     </LinkButton>
                  </div>
                  {showHelp && (
                     <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5 text-sm text-[#99A1AF] space-y-1.5">
                        <p className="text-white/90">Three steps, about 20 seconds:</p>
                        <ol className="list-decimal pl-5 space-y-1">
                           <li>
                              Open{' '}
                              <a
                                 className="text-[#A684FF] hover:underline"
                                 href={FUB_API_KEY_URL}
                                 target="_blank"
                                 rel="noreferrer"
                              >
                                 Follow Up Boss → Admin → API
                              </a>{' '}
                              (opens in a new tab).
                           </li>
                           <li>Click Create API Key and name it "Forward Flow".</li>
                           <li>Copy the key and paste it here.</li>
                        </ol>
                        <p>You need an admin or owner login in Follow Up Boss to create keys.</p>
                     </div>
                  )}
                  <div className="relative">
                     <TextInput
                        error={error}
                        shouldLeftSectionBeFocused
                        leftSection={<DataIcon color="#99A1AF" width={18} />}
                        placeholder="Paste your API key"
                        value={fubApiKey}
                        onChange={(e) => setFubApiKey(e.target.value)}
                        onKeyDown={(e) => {
                           if (e.key === 'Enter' && canContinue) void handleConnect()
                        }}
                        autoFocus
                        data-testid="fub-key"
                     />
                     {fubApiKey && !error && (
                        <div className="absolute right-3 top-2.5 text-xs font-medium">
                           {isVerifying && <span className="text-[#99A1AF]">Checking</span>}
                           {!isVerifying && isValid && (
                              <span className="text-[#00D492]">Verified</span>
                           )}
                        </div>
                     )}
                  </div>
                  <p className="text-white/40 text-xs">
                     Read-only for setup. The key is stored encrypted and can be
                     revoked in Follow Up Boss at any time.
                  </p>
               </div>
            )}
         </Card>

         <StepFooter
            action={buttonLabel}
            onAction={() => void handleConnect()}
            disabled={!canContinue}
            loading={isLoading}
            note="Next: we show you who we found in your account."
            testId="connect-fub"
         />
      </div>
   )
}
